/**
 * Track cumulative session actions for milestone detection
 *
 * This utility helps detect when users reach significant thresholds like:
 * - First product view
 * - Third product view (engaged user)
 * - Fifth product view (highly engaged user)
 *
 * Data is stored in sessionStorage and persists across page navigations
 * but resets when the browser tab is closed.
 *
 * All sessionStorage access is wrapped in try/catch to handle private
 * browsing mode and storage-full scenarios gracefully. Falls back to
 * an in-memory Map when sessionStorage is unavailable.
 */
class SessionTracker {
  private counts: Map<string, number> = new Map();
  private milestones: Map<string, boolean> = new Map();
  private storageAvailable: boolean | null = null;

  /**
   * Check if sessionStorage is available and writable.
   * Result is cached after first check.
   */
  private isStorageAvailable(): boolean {
    if (this.storageAvailable !== null) return this.storageAvailable;
    try {
      const testKey = '__session_tracker_test__';
      sessionStorage.setItem(testKey, '1');
      sessionStorage.removeItem(testKey);
      this.storageAvailable = true;
    } catch {
      this.storageAvailable = false;
    }
    return this.storageAvailable;
  }

  private storageGet(key: string): string | null {
    if (!this.isStorageAvailable()) return null;
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private storageSet(key: string, value: string): void {
    if (!this.isStorageAvailable()) return;
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage full or unavailable — continue with in-memory fallback
    }
  }

  private storageRemove(key: string): void {
    if (!this.isStorageAvailable()) return;
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore removal failures
    }
  }

  private storageKeys(): string[] {
    if (!this.isStorageAvailable()) return [];
    try {
      return Object.keys(sessionStorage);
    } catch {
      return [];
    }
  }

  /**
   * Increment a counter and return the new value
   * @param key - Counter identifier (e.g., 'product_views', 'filter_uses')
   * @returns The updated count
   */
  increment(key: string): number {
    const current = this.get(key);
    const updated = current + 1;
    this.counts.set(key, updated);
    this.storageSet(`session_${key}`, String(updated));
    return updated;
  }

  /**
   * Get the current count for a key
   * @param key - Counter identifier
   * @returns Current count (0 if never incremented)
   */
  get(key: string): number {
    if (!this.counts.has(key)) {
      const stored = this.storageGet(`session_${key}`);
      this.counts.set(key, stored ? parseInt(stored, 10) : 0);
    }
    return this.counts.get(key) || 0;
  }

  /**
   * Set a counter to a specific value
   * @param key - Counter identifier
   * @param value - Value to set
   */
  set(key: string, value: number): void {
    this.counts.set(key, value);
    this.storageSet(`session_${key}`, String(value));
  }

  /**
   * Check if a milestone has been reached (only fires once)
   * @param key - Milestone identifier (e.g., 'first_filter_milestone')
   * @returns true if this is the first time checking this milestone
   */
  checkMilestone(key: string): boolean {
    const milestoneKey = `milestone_${key}`;

    // Check in-memory first, then storage
    if (this.milestones.get(milestoneKey)) return false;

    const alreadyFired = this.storageGet(milestoneKey) === 'true';
    if (!alreadyFired) {
      this.milestones.set(milestoneKey, true);
      this.storageSet(milestoneKey, 'true');
      return true;
    }
    this.milestones.set(milestoneKey, true);
    return false;
  }

  /**
   * Reset all counters (useful for testing or explicit session restart)
   */
  reset(): void {
    this.counts.clear();
    this.milestones.clear();
    this.storageKeys().forEach((key) => {
      if (key.startsWith('session_') || key.startsWith('milestone_')) {
        this.storageRemove(key);
      }
    });
  }

  /**
   * Get all current counts (useful for debugging)
   */
  getAll(): Record<string, number> {
    const result: Record<string, number> = {};
    this.storageKeys().forEach((key) => {
      if (key.startsWith('session_')) {
        const cleanKey = key.replace('session_', '');
        result[cleanKey] = this.get(cleanKey);
      }
    });
    return result;
  }
}

export const sessionTracker = new SessionTracker();
